import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, ShieldCheck, XCircle } from 'lucide-react';

import API_BASE from '../../utils/apiBase';

const API = API_BASE;

function statusTone(key) {
  if (key === 'ready_for_work') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (key === 'onboarding') return 'border-blue-200 bg-blue-50 text-blue-800';
  if (key === 'unavailable') return 'border-slate-200 bg-slate-50 text-slate-700';
  return 'border-amber-200 bg-amber-50 text-amber-800';
}

export default function StaffIdVerifyPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let active = true;
    axios.get(`${API}/public/staff-id/${encodeURIComponent(token || '')}`)
      .then((response) => {
        if (active) setState({ loading: false, data: response.data, error: null });
      })
      .catch((error) => {
        if (!active) return;
        const detail = error.response?.status === 404
          ? 'This staff ID is not recognised.'
          : 'We could not verify this staff ID right now.';
        setState({ loading: false, data: null, error: detail });
      });
    return () => { active = false; };
  }, [token]);

  if (state.loading) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-12">
        <div className="mx-auto max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 text-slate-600">
            <Loader2 className="h-5 w-5 animate-spin" />
            Verifying staff ID…
          </div>
        </div>
      </main>
    );
  }

  if (state.error || !state.data) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-12">
        <div className="mx-auto max-w-md rounded-3xl border border-red-200 bg-white p-6 shadow-sm">
          <XCircle className="h-10 w-10 text-red-600" />
          <h1 className="mt-4 text-xl font-bold text-slate-900">Staff ID not verified</h1>
          <p className="mt-2 text-sm text-slate-600">{state.error}</p>
        </div>
      </main>
    );
  }

  const data = state.data;
  const brandColour = /^#[0-9a-fA-F]{6}$/.test(data.primary_color || '')
    ? data.primary_color
    : '#0F5C5E';
  const valid = data.verified && data.card_valid;

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg">
        <header className="px-6 py-6 text-white" style={{ backgroundColor: brandColour }}>
          <div className="flex items-center gap-3">
            {data.logo_url ? (
              <img
                src={data.logo_url}
                alt=""
                className="h-12 w-12 rounded-xl bg-white p-1 object-contain"
                onError={(event) => { event.currentTarget.style.display = 'none'; }}
              />
            ) : null}
            <div>
              <p className="text-sm font-semibold">{data.organisation_name}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-white/80">
                Staff ID verification
              </p>
            </div>
          </div>
        </header>

        <div className="p-6">
          <div className="flex items-start gap-3">
            {valid ? (
              <CheckCircle2 className="mt-0.5 h-7 w-7 shrink-0 text-emerald-600" />
            ) : (
              <ShieldCheck className="mt-0.5 h-7 w-7 shrink-0 text-amber-600" />
            )}
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                {valid ? 'Identity verified' : 'Identity found — check current status'}
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                This page reads the current CareOps record for this staff ID.
              </p>
            </div>
          </div>

          <dl className="mt-6 space-y-4">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Name</dt>
              <dd className="mt-1 text-base font-semibold text-slate-900">{data.display_name}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Role</dt>
              <dd className="mt-1 text-sm text-slate-800">{data.role}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Staff ID</dt>
              <dd className="mt-1 font-mono text-sm font-semibold text-slate-900">{data.staff_id}</dd>
            </div>
          </dl>

          <div className={`mt-6 rounded-2xl border p-4 ${statusTone(data.work_status?.key)}`}>
            <p className="text-xs font-semibold uppercase tracking-wide">Current work status</p>
            <p className="mt-1 text-sm font-bold">{data.work_status?.label || 'Status unavailable'}</p>
          </div>

          {data.expires_at ? (
            <p className="mt-4 text-xs text-slate-500">
              Card expiry: {new Date(data.expires_at).toLocaleDateString('en-GB')}
            </p>
          ) : null}

          <p className="mt-6 border-t border-slate-100 pt-4 text-xs leading-5 text-slate-500">
            This page verifies the staff identity and current high-level work status only. It does not expose DBS,
            right-to-work, health, training, address or other employee records.
          </p>
        </div>
      </div>
    </main>
  );
}
